import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
} from "@mui/material";
import { FileImage, FileVideo, File } from "lucide-react";
import axios from "axios";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AutohideSnackbar from "../../../components/AutohideSnackbar";
import axiosInstance from "../../../services/axiosInterceptors";
import { palette, theme as colorTheme } from "../../../Convoze.theme";



  const primary = colorTheme('palette', 'primary').value;
  const primaryBg = colorTheme('palette', 'primaryBg').value;
  const positive = colorTheme('palette', 'positive').value;
  const negative = colorTheme('palette', 'negative').value;
  const neutral = colorTheme('palette', 'neutral').value;


// Allowed file extensions
const allowedExtensions = [
  ".wav",
  ".mp3",
  ".m4a",
  ".aac",
  ".flac",
  ".ogg",
  ".wma",
  ".aiff",
  ".alac",
  ".opus",
];

// Styled components
const UploadBox = styled(Box)(({ theme, isDragging }) => ({
  border: "2px dashed rgba(99, 102, 241, 0.3)",
  borderRadius: 16,
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(99, 102, 241, 0.05)" : "white",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  minHeight: 100,
  "&:hover": {
    borderColor: primary,
    backgroundColor: "rgba(99, 102, 241, 0.05)",
  },
}));

const IconGroup = styled(Box)({
  display: "flex",
  gap: "4px",
  marginBottom: "24px",
});

const UploadButton = styled(Button)({
  backgroundColor: primary,
  borderRadius: "24px",
  padding: "10px 32px",
  textTransform: "none",
  fontSize: "16px",
  "&:hover": {
    backgroundColor: primary
  },
});

// File Upload Component
export default function FileUploadFormik() {
  const [isDragging, setIsDragging] = useState(false);
  const [reloadApi, setReloadApi] = useState(false);
  const fileInputRef = useRef(null);
  const [disable, setDisable] = useState(false);
  const token = localStorage.getItem("token");
  const [filesLength, setFilesLength] = useState(0);
  //snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState(false);

  // Function to validate allowed files
  const validateFiles = (files) => {
    const validFiles = files.filter((file) =>
      allowedExtensions.includes(file.name.slice(file.name.lastIndexOf(".")))
    );

    if (validFiles.length !== files.length) {
      alert(
        "Some files have unsupported formats. Only .wav, .mp3, .m4a, .aac, .flac, .ogg, .wma, .aiff, .alac, and .opus files are allowed."
      );
    }

    return validFiles;
  };

  // Function to upload files
  const uploadFiles = async (files) => {
    setFilesLength(files?.length);
    setDisable(true);
    if (!files || files.length === 0) {
      console.error("No files selected for upload");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axiosInstance.post(
        apiEndPointsConfig.upload,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setSnackbarMessage("Files uploaded successfully!");
      setSnackbarOpen(true);
      setReloadApi(true);
      setDisable(false);
      
      formik.setFieldValue("files", []);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFilesLength(0); 
      setWarningSnackbarOpen(false);
    } catch (error) {
      setSnackbarMessage(error.response.data.status.value);
      setSnackbarOpen(true);
      setDisable(false);
      console.error("Upload failed:", error.response.data.status.value);
    }
  };

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      files: [],
    },
    onSubmit: (data) => {
      if (data?.files?.length > 0) {
        setSnackbarMessage(
          `Uploading ${data.files.length} audio file${
            data.files.length > 1 ? "s" : ""
          }...`
        );
        setSnackbarOpen(true);

        uploadFiles(data.files);
      }
    },
  });

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = validateFiles(files);
    setFilesLength(validFiles.length);
    formik.setFieldValue("files", validFiles);

    // Show warning if 10 or more files selected
    if (validFiles.length >= 10) {
      setWarningSnackbarOpen(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(files);
    setFilesLength(validFiles.length);
    formik.setFieldValue("files", validFiles);

    // Show warning if 10 or more files dropped
    if (validFiles.length >= 10) {
      setWarningSnackbarOpen(true);
    }
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto" }}>
      <form onSubmit={formik.handleSubmit}>
        <UploadBox
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload files"
        >
          <IconGroup>
            <FileImage size={32} color={primary} />
            <File size={32} color={primary}/>
            <FileVideo size={32} color={primary}/>
          </IconGroup>

          <Typography variant="h5" component="div" sx={{ mb: 1 }}>
            Drag & drop{" "}
            <Box component="span" sx={{ color:primary }}>
              audio&nbsp;
            </Box>
            <Box component="span" sx={{ color: primary }}>
              files
            </Box>
            , or any{" "}
            <Box component="span" sx={{ color:primary }}>
              file
            </Box>
          </Typography>
          {formik.values.files.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: positive,
                fontWeight: 500,
                mb: 1,
              }}
            >
              {formik.values.files.length} audio file
              {formik.values.files.length > 1 ? "s" : ""} selected
            </Typography>
          )}
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            or{" "}
            <Box
              component="span"
              sx={{
                color: primary,
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              browse files
            </Box>{" "}
            on your computer
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInput}
            style={{ display: "none" }}
            multiple
          />
        </UploadBox>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.4rem",
              fontWeight: 500,
              color: "#424242",
              // mb: 3,
            }}
            // gutterBottom
          ></Typography>
          {disable == false ? (
            <UploadButton
              type="submit"
              variant="contained"
              disabled={formik.values.files.length === 0}
              sx={{
                bgcolor: primary,
                color: "white",
                textTransform: "none",
                fontSize: "1rem",
                height: "40px",
                mb: 3,
                borderRadius: "12px",
                "&:hover": {
                  bgcolor: primary,
                },
                mt: 2,
              }}
            >
              Upload
            </UploadButton>
          ) : (
            <UploadButton
              type="submit"
              variant="contained"
              disabled={true}
              sx={{
                bgcolor: primary,
                color: "white",
                textTransform: "none",
                fontSize: "1rem",
                height: "40px",
                mb: 3,
                borderRadius: "12px",
                "&:hover": {
                  bgcolor: primary,
                },
                mt: 2,
              }}
            >
              Uploading{" "}
              {formik.values.files.length > 0 &&
                `(${formik.values.files.length})`}{" "}
              &nbsp;&nbsp;
              <CircularProgress size={20} />
            </UploadButton>
          )}
        </Box>
      </form>
      {/* Snackbar Component */}
      <AutohideSnackbar
        message={snackbarMessage}
        open={snackbarOpen}
        fileCount={filesLength}
        onClose={() => setSnackbarOpen(false)}
      />
      <AutohideSnackbar
        message="You're uploading 10 or more audio files. This might take some time."
        open={warningSnackbarOpen}
        onClose={() => setWarningSnackbarOpen(false)}
        severity="warning"
      />
    </Box>
  );
}
